import { Star, BadgeCheck, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Professional } from '@/types';

interface ProfessionalCardProps {
  professional: Professional;
  onClick?: () => void;
  className?: string;
}

export function ProfessionalCard({ professional, onClick, className }: ProfessionalCardProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-AO').format(price);
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'group card-kuiva cursor-pointer',
        className
      )}
    >
      <div className="flex items-start gap-4">
        {/* Photo */}
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-kuiva-green">
            <img
              src={professional.photo}
              alt={professional.name}
              className="h-full w-full object-cover transition-transform duration-700 ease-kuiva group-hover:scale-[1.06]"
              onError={(e) => {
                (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(professional.name)}&background=random&size=160`;
              }}
            />
          </div>
          {professional.isVerified && (
            <div className="absolute -bottom-1 -right-1 bg-kuiva-blue text-white p-1 rounded-full">
              <BadgeCheck className="w-4 h-4" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground truncate">{professional.name}</h3>
          </div>
          
          <p className="text-sm text-muted-foreground mb-2">{professional.specialty}</p>
          
          {/* Rating */}
          <div className="flex items-center gap-1 mb-3">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-medium">{professional.rating}</span>
            <span className="text-sm text-muted-foreground">({professional.reviewCount})</span>
          </div>

          {/* Specialties */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {professional.specialties.slice(0, 3).map((specialty) => (
              <span
                key={specialty}
                className="px-2 py-0.5 bg-kuiva-light text-xs rounded-full text-foreground-secondary"
              >
                {specialty}
              </span>
            ))}
          </div>

          {/* Price and Availability */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-kuiva-blue font-bold min-w-0 break-words">
              {formatPrice(professional.pricePerSession)} {professional.currency}
              <span className="text-xs font-normal text-muted-foreground">/sessão</span>
            </div>
            
            {professional.isAvailableToday && (
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium shrink-0">
                <Calendar className="w-3 h-3" />
                Hoje
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
